/**
 * Sprint P32 · Cockpit camera helpers.
 * - FitCamera         : fits camera to a target Box3 (professional 6-mode fit)
 * - CameraBridge      : exposes .capture() / .apply() to the parent
 * - CameraTelemetry   : per-frame camera position/target readback
 */
import { useEffect, useRef } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { VIEW_DIRECTIONS, fitDistanceForBox } from "@/lib/sceneCoords";


export function FitCamera({ fitRequest, onDone, onFail }) {
  const { camera, controls, gl } = useThree();
  const lastRef = useRef(null);

  useEffect(() => {
    if (!fitRequest || fitRequest === lastRef.current) return;
    lastRef.current = fitRequest;
    const { box, direction = VIEW_DIRECTIONS.iso_ne, padding = 1.35 } = fitRequest;
    if (!box || box.isEmpty?.()) { onFail?.("empty bounds"); return; }

    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        const width  = gl.domElement.clientWidth;
        const height = gl.domElement.clientHeight;
        if (width > 0 && height > 0) {
          camera.aspect = width / height;
        }
        const fit = fitDistanceForBox(box, camera, padding);
        if (!fit) { onFail?.("fit calc failed"); return; }
        const view = direction.clone().normalize();
        camera.position.copy(fit.center.clone().add(view.multiplyScalar(fit.distance)));
        camera.near = fit.near;
        camera.far  = fit.far;
        camera.updateProjectionMatrix();
        if (controls) {
          controls.target.copy(fit.center);
          controls.minDistance = Math.max(fit.maxSize * 0.01, 0.1);
          controls.maxDistance = Math.max(fit.maxSize * 100, 10000);
          controls.update();
        }
        onDone?.({
          center: fit.center.clone(),
          size:   fit.size.clone(),
          distance: fit.distance,
          near: fit.near,
          far:  fit.far,
        });
      });
      return () => cancelAnimationFrame(raf2);
    });
    return () => cancelAnimationFrame(raf1);
  }, [fitRequest, camera, controls, gl, onDone, onFail]);

  return null;
}


export function CameraBridge({ onReady }) {
  const { camera, controls } = useThree();
  useEffect(() => {
    onReady({
      capture: () => ({
        position: [camera.position.x, camera.position.y, camera.position.z],
        target:   controls ? [controls.target.x, controls.target.y, controls.target.z] : [0, 0, 0],
        fov:      camera.fov,
        zoom:     camera.zoom,
        projection: camera.isOrthographicCamera ? "ortho" : "perspective",
      }),
      apply: (cam) => {
        if (!cam) return;
        if (Array.isArray(cam.position) && cam.position.length === 3)
          camera.position.set(cam.position[0], cam.position[1], cam.position[2]);
        if (controls && Array.isArray(cam.target) && cam.target.length === 3) {
          controls.target.set(cam.target[0], cam.target[1], cam.target[2]);
          controls.update();
        }
        if (Number.isFinite(cam.fov)) camera.fov = cam.fov;
        camera.updateProjectionMatrix();
      },
    });
  }, [camera, controls, onReady]);
  return null;
}


export function CameraTelemetry({ onUpdate }) {
  const { camera, controls } = useThree();
  useFrame(() => {
    onUpdate({
      pos:    camera.position.clone(),
      target: controls ? controls.target.clone() : new THREE.Vector3(),
      near:   camera.near,
      far:    camera.far,
    });
  });
  return null;
}
